import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  useColorModeValue,
  useDisclosure,
  useToast,
  Select,
  FormLabel,
  Text,
} from "components-lib";
import { FiRefreshCcw } from "react-icons/fi";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { SocketProvider } from "context/SocketProvider";
import AddFrontdeskModal from "views/admin/doctorsList/AddFrontdeskModal";
import { createFrontdeskFormDefaults } from "views/admin/shared/formDefaults";
import { useFrontdeskList } from "./hooks/useFrontdeskList";
import { frontdeskColumnsData } from "views/admin/default/variables/columnsData";
import { useGlobalFilters } from "contexts/GlobalFiltersContext";
import DoctorForm from "../doctorsList/DoctorForm";
import AddClinicModal from "../doctorsList/AddClinicModal";
import { axiosInstance } from "config/api";
import FrontdeskEditModal from "./FrontdeskEditModal";

export default function FrontdeskList() {
  const toast = useToast();
  const globalFiltersContext = useGlobalFilters();

  // Extract global filters - call hooks first (before conditional return)
  const {
    filters: globalFilters = {},
    lists: globalLists = {},
    actions: globalActions = {},
  } = globalFiltersContext || {};

  const gClinicId = globalFilters?.clinicId ?? null;

  const {
    frontdesks,
    setFrontdesks,
    isLoading,
    error,
    refreshFrontdesks,
  } = useFrontdeskList(gClinicId);

  const clinics = globalLists?.clinics ?? [];
  const {
    isOpen: isAddFrontdeskOpen,
    onOpen: onOpenAddFrontdeskModal,
    onClose: onCloseAddFrontdeskModalInternal,
  } = useDisclosure();

  const {
    isOpen: isDoctorFormOpen,
    onOpen: onDoctorFormOpen,
    onClose: onDoctorFormClose,
  } = useDisclosure();

  const [isAddClinicOpen, setIsAddClinicOpen] = useState(false);
  const {
    isOpen: isEditFrontdeskOpen,
    onOpen: onOpenEditFrontdeskModal,
    onClose: onCloseEditFrontdeskModalInternal,
  } = useDisclosure();

  const onOpenAddClinicModal = () => setIsAddClinicOpen(true);
  const onCloseAddClinicModal = () => setIsAddClinicOpen(false);

  const handleAddDoctor = (newDoctor) => {
    // Adding a doctor doesn't affect the frontdesk list, so no action needed
  };

  const [frontdeskForm, setFrontdeskForm] = useState(() =>
    createFrontdeskFormDefaults(),
  );
  const [selectedFrontdesk, setSelectedFrontdesk] = useState(null);

  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const handleFrontdeskModalClose = useCallback(() => {
    setFrontdeskForm(createFrontdeskFormDefaults());
    onCloseAddFrontdeskModalInternal();
  }, [onCloseAddFrontdeskModalInternal]);

  const handleFrontdeskCreated = useCallback(() => {
    setFrontdeskForm(createFrontdeskFormDefaults());
    // Ensure the added frontdesk is prefilled with clinic if one is selected
    setFrontdeskForm((prev) => ({
      ...prev,
      clinic_id: gClinicId || prev.clinic_id || "",
    }));
    refreshFrontdesks();
  }, [refreshFrontdesks, gClinicId]);

  const handleFrontdeskEdit = useCallback(
    (frontdesk) => {
      setSelectedFrontdesk(frontdesk);
      onOpenEditFrontdeskModal();
    },
    [onOpenEditFrontdeskModal],
  );

  const handleCloseEditFrontdesk = useCallback(() => {
    setSelectedFrontdesk(null);
    onCloseEditFrontdeskModalInternal();
  }, [onCloseEditFrontdeskModalInternal]);

  const deleteFrontdesk = useCallback(async (frontdeskId) => {
    const response = await axiosInstance.delete(`/api/frontdesk/${frontdeskId}`, {
      validateStatus: (status) => status < 500,
    });

    if (response.status >= 200 && response.status < 300) {
      return true;
    }

    throw new Error(`Unexpected status ${response.status}`);
  }, []);

  const handleFrontdeskDelete = useCallback(
    async (frontdesk) => {
      if (!frontdesk?.id) return;

      const confirmed = window.confirm(
        `Delete frontdesk "${frontdesk.name || "this user"}"? This action cannot be undone.`,
      );
      if (!confirmed) return;

      try {
        await deleteFrontdesk(frontdesk.id);
        toast({
          title: "Frontdesk removed",
          description: "Frontdesk deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        refreshFrontdesks();
      } catch (error) {
        console.error("Error deleting frontdesk:", error);
        toast({
          title: "Delete failed",
          description:
            error.response?.data?.message ||
            "Could not delete frontdesk. Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [deleteFrontdesk, toast, refreshFrontdesks],
  );

  const columnsData = useMemo(() => frontdeskColumnsData, []);

  const tableData = useMemo(
    () =>
      frontdesks.map((frontdesk) => ({
        ...frontdesk,
        onEdit: () => handleFrontdeskEdit(frontdesk),
        onDelete: () => handleFrontdeskDelete(frontdesk),
      })),
    [frontdesks, handleFrontdeskDelete, handleFrontdeskEdit],
  );

  // Handle case where GlobalFiltersProvider might not be in scope (after hooks)
  if (!globalFiltersContext) {
    return (
      <Box p={4}>
        <Text color="red.500">
          Error: GlobalFiltersProvider not found. Please ensure AdminLayout wraps this component.
        </Text>
      </Box>
    );
  }

  return (
    <SocketProvider>
      <Box
        pt={{ base: "130px", md: "80px", xl: "80px" }}
        w="100%"
        overflowX="auto"
        bg={boxBg}
        p={4}
        borderRadius="md"
      >
        <Flex justify="space-between" mb="4" gap={4}>
          <Flex gap={4} align="start">
            <Box>
              <Select
                placeholder="All clinics"
                value={gClinicId ?? ""}
                onChange={(e) => {
                  const val = e.target.value || null;
                  if (typeof globalActions?.setClinicId === "function") {
                    globalActions.setClinicId(val);
                  }
                }}
                width="220px"
              >
                {clinics.map((c) => (
                  <option key={c.id || c.clinic_id || c._id} value={c.id || c.clinic_id || c._id}>
                    {c.name || c.clinic_name || c.clinic}
                  </option>
                ))}
              </Select>
            </Box>
            {/* {gClinicId && (
              <Text fontSize="sm" color="gray.500" ml={2} alignSelf="end">
                Showing frontdesks for clinic #{gClinicId}
              </Text>
            )} */}
            {/* <IconButton
              aria-label="Refresh frontdesk list"
              icon={<FiRefreshCcw />}
              onClick={refreshFrontdesks}
              isRound
              isDisabled={isLoading}
            /> */}
          </Flex>
          <Flex gap={4}>
            <Button
              colorScheme="orange"
              variant="solid"
              onClick={onOpenAddClinicModal}
            >
              Add Clinic
            </Button>
            <Button colorScheme="teal" onClick={onDoctorFormOpen}>
              Add Doctor
            </Button>
            <Button
              colorScheme="purple"
              onClick={() => {
                // prefill form's clinic_id from global selection if available
                setFrontdeskForm((prev) => ({
                  ...prev,
                  clinic_id: gClinicId || prev.clinic_id || "",
                }));
                onOpenAddFrontdeskModal();
              }}
            >
              Add Frontdesk
            </Button>
          </Flex>
        </Flex>
        <ComplexTable
          columnsData={columnsData}
          tableData={tableData}
          setTableDataComplex={setFrontdesks}
          isLoading={isLoading}
        />
        <AddFrontdeskModal
          isOpen={isAddFrontdeskOpen}
          onClose={handleFrontdeskModalClose}
          frontdeskForm={frontdeskForm}
          setFrontdeskForm={setFrontdeskForm}
          frontdeskLoading={false}
          onFrontdeskCreated={handleFrontdeskCreated}
          preselectedClinicId={gClinicId}
        />
        <DoctorForm
          isOpen={isDoctorFormOpen}
          onClose={onDoctorFormClose}
          handleAddDoctor={handleAddDoctor}
          preselectedClinicId={gClinicId}
        />
        <AddClinicModal
          isOpen={isAddClinicOpen}
          onClose={onCloseAddClinicModal}
        />
        <FrontdeskEditModal
          isOpen={isEditFrontdeskOpen}
          onClose={handleCloseEditFrontdesk}
          frontdesk={selectedFrontdesk}
          onFrontdeskUpdated={refreshFrontdesks}
        />
      </Box>
    </SocketProvider>
  );
}
